import type { ModalPropsMap, ModalType } from '../../props';

import { createModalErrorReporter, type ModalErrorLogger, type ModalErrorReporter } from '../../errors';
import { setModalHostInternals } from './modalHostInternals';
import { createModalInstanceRegistry } from './modalInstanceRegistry';
import { isDuplicatePackageInstance } from './modalPackageInstance';
import { createModalRegistry, DEFAULT_MODAL_LOAD_TIMEOUT_MS, type ModalRegistry } from '../../registry';
import { createModalStackStore, type ModalStackItem, type ModalStackStore } from '../../stack';

export const DEFAULT_UNRESOLVED_TIMEOUT_MS = 5000;

export type OpenModalArgs<Props> = {} extends Props
  ? [props?: Props, onCloseCallback?: () => void]
  : [props: Props, onCloseCallback?: () => void];

export interface ModalHostApi {
  openModal: <T extends ModalType>(type: T, ...args: OpenModalArgs<ModalPropsMap[T]>) => string;
  closeModal: (id: string) => void;
  replaceModal: <T extends ModalType>(id: string, type: T, ...args: OpenModalArgs<ModalPropsMap[T]>) => void;
  closeAllModals: () => void;
}

export interface ModalHost extends ModalHostApi {
  registry: ModalRegistry;
  dispose: () => void;
}

export interface ModalHostOptions {
  stackStore?: ModalStackStore;
  isDev?: boolean;
  reportError?: ModalErrorReporter;
  logError?: ModalErrorLogger;
  unresolvedTimeoutMs?: number;
  loadTimeoutMs?: number;
}

export const createModalHost = ({
  stackStore = createModalStackStore(),
  isDev = false,
  reportError: reportErrorOption,
  logError,
  unresolvedTimeoutMs = DEFAULT_UNRESOLVED_TIMEOUT_MS,
  loadTimeoutMs = DEFAULT_MODAL_LOAD_TIMEOUT_MS,
}: ModalHostOptions = {}): ModalHost => {
  const reportError = createModalErrorReporter({ reportError: reportErrorOption, logError });
  const registry = createModalRegistry({ isDev, reportError, loadTimeoutMs });
  const instances = createModalInstanceRegistry();
  let releasedStack: ModalStackItem[] | null = null;

  if (isDuplicatePackageInstance()) {
    reportError(new Error('Another copy of @zvuk-b2b/modal-host is loaded, share the package as a singleton.'), {
      reason: 'duplicate-package',
    });
  }

  const takeCloseCallbacks = (ids: string[]) =>
    ids.map((id) => {
      const onCloseCallback = instances.getCloseCallback(id);

      instances.delete(id);

      return { id, onCloseCallback };
    });

  const callCloseCallbacks = (callbacks: ReturnType<typeof takeCloseCallbacks>) => {
    callbacks.forEach(({ id, onCloseCallback }) => {
      try {
        onCloseCallback?.();
      } catch (error) {
        reportError(error, { reason: 'close', modalId: id });
      }
    });
  };

  const releaseClosedInstances = () => {
    const stack = stackStore.getStack();

    if (stack === releasedStack) return;

    releasedStack = stack;

    const openIds = new Set(stack.map(({ id }) => id));
    const closedIds = instances.getIds().filter((id) => !openIds.has(id));

    callCloseCallbacks(takeCloseCallbacks(closedIds));
  };

  const unsubscribeStack = stackStore.subscribe(releaseClosedInstances);

  const openModal: ModalHostApi['openModal'] = (type, ...args) => {
    const [props, onCloseCallback] = args;
    const id = instances.createId();

    instances.set(id, { props: props ?? {}, onCloseCallback });
    stackStore.push({ id, type, openedAt: Date.now() });

    return id;
  };

  const closeModal: ModalHostApi['closeModal'] = (id) => {
    const callbacks = takeCloseCallbacks([id]);

    stackStore.remove(id);
    callCloseCallbacks(callbacks);
  };

  const discardModal = (id: string) => {
    instances.delete(id);
    stackStore.remove(id);
  };

  const replaceModal: ModalHostApi['replaceModal'] = (id, type, ...args) => {
    const [props, onCloseCallback] = args;

    if (!stackStore.getStack().some((modal) => modal.id === id)) return;

    instances.set(id, { props: props ?? {}, onCloseCallback });
    stackStore.replace({ id, type, openedAt: Date.now() });
  };

  const closeAllModals: ModalHostApi['closeAllModals'] = () => {
    const callbacks = takeCloseCallbacks(stackStore.getStack().map(({ id }) => id));

    stackStore.clear();
    callCloseCallbacks(callbacks);
  };

  const dispose = () => {
    unsubscribeStack();
    closeAllModals();
  };

  const host: ModalHost = {
    openModal,
    closeModal,
    replaceModal,
    closeAllModals,
    registry,
    dispose,
  };

  setModalHostInternals(host, { stackStore, instances, unresolvedTimeoutMs, reportError, discardModal });

  return host;
};
